import type { ParticipantNote } from '@seen/core/models/participantNote'
import { resolveId } from '@seen/core/references'
import { Button } from '@seen/ui'
import { IconChevronRight, IconDots } from '@tabler/icons-react'
import type { JSX } from 'react'
import { LinkCell } from '#src/components/core/data-table/LinkCell.tsx'
import { DropdownMenu } from '#src/components/core/dropdown-menu/DropdownMenu.tsx'
import { DropdownMenuContent } from '#src/components/core/dropdown-menu/DropdownMenuContent.tsx'
import { DropdownMenuDeleteItem } from '#src/components/core/dropdown-menu/DropdownMenuDeleteItem.tsx'
import { DropdownMenuTrigger } from '#src/components/core/dropdown-menu/DropdownMenuTrigger.tsx'
import { HoverDate } from '#src/components/core/hover-card/HoverDate.tsx'
import { ParticipantName } from '#src/components/ParticipantName.tsx'
import { useParticipantNoteTitle } from '#src/hooks/useParticipantNoteTitle.ts'
import { usePatchMutation } from '#src/hooks/usePatchMutation.ts'

export function ParticipantNoteTitleCell({
  participantNote,
}: {
  participantNote: ParticipantNote
}): JSX.Element {
  const title = useParticipantNoteTitle(participantNote)
  return (
    <LinkCell
      to={`/Patient/${resolveId(participantNote.participant)}/visit-notes/${participantNote.id}`}
    >
      <span className="line-clamp-1">{title}</span>
    </LinkCell>
  )
}

export function ParticipantNoteParticipantCell({
  participantNote,
}: {
  participantNote: ParticipantNote
}): JSX.Element {
  return (
    <LinkCell
      to={`/Patient/${resolveId(participantNote.participant)}/visit-notes/${participantNote.id}`}
    >
      <ParticipantName participant={participantNote.participant} />
    </LinkCell>
  )
}

export function ParticipantNoteLastUpdatedCell({
  participantNote,
}: {
  participantNote: ParticipantNote
}): JSX.Element {
  return (
    <LinkCell
      to={`/Patient/${resolveId(participantNote.participant)}/visit-notes/${participantNote.id}`}
    >
      <HoverDate date={participantNote.lastUpdated} />
    </LinkCell>
  )
}

export function ParticipantNoteActionsCell({
  participantNote,
}: {
  participantNote: ParticipantNote
}): JSX.Element {
  const mutate = usePatchMutation<ParticipantNote>().mutate
  const title = useParticipantNoteTitle(participantNote)
  return (
    <LinkCell
      to={`/Patient/${resolveId(participantNote.participant)}/visit-notes/${participantNote.id}`}
      className="flex items-center justify-end gap-1"
    >
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" className="-my-3">
            <IconDots className="size-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
          <DropdownMenuDeleteItem
            deleteConfirmationProps={{
              title: `Delete "${title}"?`,
              description:
                'Deleted notes can still be recovered by contacting our team.',
              onConfirm: () => {
                mutate({
                  instance: participantNote,
                  mutateFn: (t) => {
                    t.status = 'entered-in-error'
                  },
                })
              },
            }}
          />
        </DropdownMenuContent>
      </DropdownMenu>
      <Button variant="ghost" className="-my-3 -mr-2" size="icon">
        <IconChevronRight className="size-4" />
      </Button>
    </LinkCell>
  )
}
